/* eslint-disable no-inline-styles/no-inline-styles */

import { IconImage } from '@/core/features';
import { useMetaDataState } from '@/core/features/a-dynamic-page/hooks/meta-data-state';
import { useIsIos } from '@/core/features/app/app-atoms';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import ConditionalLinkWrapper from '@/core/features/link/conditional-link-wrapper/conditional-link-wrapper';
import { savePageScrollPosition } from '@/core/features/scroll/page-scroll-restoration/scroll-history';
import { scrollToTop } from '@/core/features/scroll/scroll-service';
import { useSetAtom } from '@/core/features/store/atom-store';
import zIndex from '@/core/features/styles/generated/z-index';
import useMapOverlayHistoryState from '@/features/map/map-container/use-map-overlay-history';
import { showMainContentAtom } from '@/features/page-layouts/desktop/desktop-map-search-layout';

export default function MapButtonSticky({
    className,
    iconUrl,
    linkUrl,
    onClick,
    position = 'fixed',
    title,
}: {
    className?: string;
    iconUrl: string;
    linkUrl: null | string;
    onClick?: () => void;
    position: 'absolute' | 'fixed';
    title: string;
}) {
    const setShowMainContent = useSetAtom(showMainContentAtom);
    const { saveMapStateToHistoryState } = useMapOverlayHistoryState();
    const meta = useMetaDataState();
    const isSearchPage = meta?.layoutId === 'Desktop_Map_Search_V1';

    const _onClick = () => {
        if (isDesktopView) {
            setShowMainContent(false);
            savePageScrollPosition();
            scrollToTop('instant');
        }
        if (!linkUrl) {
            saveMapStateToHistoryState({
                bottomSheetScrollPosition: 0,
                debugNamespace: 'mapButton',
                isBottomSheetOpen: false,
            });
        }
        onClick?.();
    };

    const isIos = useIsIos();
    const { isApp, isDesktopView } = useDeviceoutput();

    const isIosWebviewFixed = isIos && isApp && position === 'fixed'; // handle tap bar on activity spot page webview -.-

    return (
        <ConditionalLinkWrapper
            className={`flex-center full-width width-100 height-100 ${isDesktopView ? 'sticky pointer' : position} ${className ?? ''}`}
            href={linkUrl}
            linkType={'internal'}
            style={{
                bottom: isIosWebviewFixed ? 'calc(40px + env(safe-area-inset-bottom, 0))' : 40,
                display: 'flex', // needed for specificity
                height: isDesktopView ? 'auto' : 0,
                zIndex: zIndex('mapButton'),
            }}
            useWrapper={true}
        >
            <div
                className={'mapButton flex-center text-white gap-10 bg-check24Blue'}
                data-qa-id={'qa-map-button-sticky'}
                onClick={_onClick}
            >
                <IconImage
                    height={17}
                    url={iconUrl}
                    width={17}
                />
                <div className={'font-family-arial'}>{title}</div>
            </div>
            <style jsx>{`
                .mapButton {
                    font-size: 14px;
                    font-weight: bold;
                    padding: 10px 15px;
                    border-radius: 20px;
                }

                :global(.body--desktop) .mapButton {
                    font-size: 18px;
                    font-weight: normal;
                    padding: 15px 25px;
                    border-radius: 40px;
                    flex-direction: row-reverse;
                }

                @media only screen and (min-width: 1365px) {
                    :global(.body--desktop) .mapButton {
                        ${isSearchPage ? 'display: none;' : ''}
                    }
                }
            `}</style>
        </ConditionalLinkWrapper>
    );
}
